/**
 * Secao "Formas de pagamento" de /comercial/configuracoes (Tarefa F2).
 *
 * CRUD de CondicaoPagamento com parcelas por EVENTO do processo (Tarefa B2):
 * cada parcela tem percentual + ancora (Aprovacao | Inicio da producao |
 * Entrega | Instalacao) + offset em dias + descricao opcional. A soma dos
 * percentuais precisa fechar 100% (validado no cliente antes do envio; o
 * backend revalida). Condicao sem parcelas continua valida (modo legado por
 * dias corridos a partir da aprovacao).
 *
 * Endpoints: /api/condicoes-pagamento (serializer nested de `parcelas` —
 * a lista enviada substitui o conjunto inteiro).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FiEdit2, FiPlus, FiStar, FiTrash2 } from "react-icons/fi";

import AsyncButton from "../../../components/AsyncButton";
import SlideOverPanel from "../../../components/SlideOverPanel";
import { useAuth } from "../../../context/AuthContext";
import {
  ANCORA_PARCELA_LABEL,
  atualizarCondicaoPagamento,
  criarCondicaoPagamento,
  desativarCondicaoPagamento,
  fmtPercentualCurto,
  listarCondicoesPagamento,
  resumirParcelasCondicao,
} from "../../../services/financeiro";

import {
  Card,
  EmptyMsg,
  ErrorMsg,
  Field,
  InlineHint,
  Input,
  Pill,
  SecondaryButton,
  SectionTitle,
  Select,
  Table,
  TableWrapper,
  Textarea,
} from "../../Orcamentos/styles";

const ANCORAS = Object.keys(ANCORA_PARCELA_LABEL);
const fmtPct = fmtPercentualCurto;
const resumirParcelas = resumirParcelasCondicao;

const PARCELA_VAZIA = { percentual: "", ancora: "APROVACAO", offset_dias: 0, descricao: "" };

function somaParcelas(parcelas) {
  const soma = parcelas.reduce((acc, p) => acc + (Number(p.percentual) || 0), 0);
  return Math.round(soma * 100) / 100;
}

function FormaPagamentoForm({ inicial, unidadeId, onSave, onCancel, onDelete }) {
  const [dados, setDados] = useState({
    nome: inicial?.nome || "",
    descricao: inicial?.descricao || "",
    taxa_pagamento_percentual: inicial?.taxa_pagamento_percentual ?? "0",
    contagem_dias: inicial?.contagem_dias || "CORRIDOS",
    favorita: !!inicial?.favorita,
  });
  const [parcelas, setParcelas] = useState(
    (inicial?.parcelas || [])
      .slice()
      .sort((a, b) => a.ordem - b.ordem)
      .map((p) => ({
        percentual: p.percentual,
        ancora: p.ancora,
        offset_dias: p.offset_dias,
        descricao: p.descricao || "",
      })),
  );
  const [erro, setErro] = useState(null);

  const soma = somaParcelas(parcelas);
  const somaOk = parcelas.length === 0 || soma === 100;

  const setParcela = (i, campo, valor) =>
    setParcelas((prev) => prev.map((p, idx) => (idx === i ? { ...p, [campo]: valor } : p)));

  const salvar = async () => {
    setErro(null);
    if (!dados.nome.trim()) {
      setErro("Informe o nome da forma de pagamento.");
      return;
    }
    if (parcelas.length > 0 && soma !== 100) {
      setErro(`A soma dos percentuais das parcelas deve ser 100% (atual: ${fmtPct(soma)}%).`);
      return;
    }
    const payload = {
      nome: dados.nome.trim(),
      descricao: dados.descricao.trim(),
      taxa_pagamento_percentual: String(dados.taxa_pagamento_percentual || "0"),
      contagem_dias: dados.contagem_dias,
      favorita: dados.favorita,
      numero_parcelas: Math.max(1, parcelas.length || Number(inicial?.numero_parcelas) || 1),
      parcelas: parcelas.map((p, i) => ({
        ordem: i + 1,
        percentual: String(p.percentual),
        ancora: p.ancora,
        offset_dias: Number(p.offset_dias) || 0,
        descricao: (p.descricao || "").trim(),
      })),
    };
    // Na EDICAO a unidade ja e conhecida e vai junto; na CRIACAO fica de
    // fora de proposito — o backend deriva do usuario (D-6C). A tela nao
    // tem como saber a unidade certa quando ha mais de uma.
    if (unidadeId) payload.unidade = unidadeId;
    try {
      await onSave(payload);
    } catch (err) {
      setErro(err?.payload?.detalhe || err?.message || "Erro ao salvar.");
      throw err; // AsyncButton mostra o toast de erro tambem
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <Field>
        Nome *
        <Input
          value={dados.nome}
          onChange={(e) => setDados({ ...dados, nome: e.target.value })}
          maxLength={120}
          placeholder="Ex.: 70/15/15 por etapa, À vista, 12x cartão"
        />
      </Field>

      <Field>
        Texto exibido na proposta
        <Textarea
          rows={2}
          value={dados.descricao}
          onChange={(e) => setDados({ ...dados, descricao: e.target.value })}
          maxLength={240}
          placeholder="Ex.: 50% na data da venda, 25% no início da montagem e 25% na montagem final."
        />
        <InlineHint>
          Aparece embaixo do select no orçamento e vira o texto da forma de
          pagamento na proposta. Vazio = o sistema resume as parcelas
          automaticamente.
        </InlineHint>
      </Field>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <Field>
          Taxa do pagamento (%)
          <Input
            type="number"
            step="0.01"
            min="0"
            max="99.99"
            value={dados.taxa_pagamento_percentual}
            onChange={(e) => setDados({ ...dados, taxa_pagamento_percentual: e.target.value })}
          />
          <InlineHint>
            Retida pelo meio de pagamento (MDR + antecipação). Entra como s2
            no motor divisor. Boleto e dinheiro = 0.
          </InlineHint>
        </Field>
        <Field>
          Contagem dos dias
          <Select
            value={dados.contagem_dias}
            onChange={(e) => setDados({ ...dados, contagem_dias: e.target.value })}
          >
            <option value="CORRIDOS">Dias corridos</option>
            <option value="UTEIS">Dias úteis (seg-sex)</option>
          </Select>
          <InlineHint>Aplica aos offsets das parcelas abaixo.</InlineHint>
        </Field>
      </div>

      <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: "0.88rem" }}>
        <input
          type="checkbox"
          checked={dados.favorita}
          onChange={(e) => setDados({ ...dados, favorita: e.target.checked })}
        />
        Favorita (aparece primeiro no select do orçamento)
      </label>

      <Field>
        Parcelas por evento do processo
        <TableWrapper>
          <Table>
            <colgroup>
              <col style={{ width: 90 }} />
              <col />
              <col style={{ width: 90 }} />
              <col />
              <col style={{ width: 40 }} />
            </colgroup>
            <thead>
              <tr>
                <th>%</th>
                <th>Âncora</th>
                <th>Offset (d)</th>
                <th>Descrição</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {parcelas.length === 0 && (
                <tr>
                  <td colSpan={5} style={{ fontSize: "0.82rem", color: "var(--ink-3, inherit)" }}>
                    Sem parcelas por evento — modo legado (rateio igual por dias
                    corridos a partir da aprovação).
                  </td>
                </tr>
              )}
              {parcelas.map((p, i) => (
                <tr key={i}>
                  <td>
                    <Input
                      type="number"
                      step="0.01"
                      min="0.01"
                      max="100"
                      style={{ width: 76 }}
                      value={p.percentual}
                      onChange={(e) => setParcela(i, "percentual", e.target.value)}
                      aria-label={`Percentual da parcela ${i + 1}`}
                    />
                  </td>
                  <td>
                    <Select
                      value={p.ancora}
                      onChange={(e) => setParcela(i, "ancora", e.target.value)}
                      aria-label={`Âncora da parcela ${i + 1}`}
                    >
                      {ANCORAS.map((a) => (
                        <option key={a} value={a}>{ANCORA_PARCELA_LABEL[a]}</option>
                      ))}
                    </Select>
                  </td>
                  <td>
                    <Input
                      type="number"
                      step="1"
                      style={{ width: 72 }}
                      value={p.offset_dias}
                      onChange={(e) => setParcela(i, "offset_dias", e.target.value)}
                      aria-label={`Offset em dias da parcela ${i + 1}`}
                    />
                  </td>
                  <td>
                    <Input
                      value={p.descricao}
                      maxLength={120}
                      placeholder="Ex.: Entrada"
                      onChange={(e) => setParcela(i, "descricao", e.target.value)}
                      aria-label={`Descrição da parcela ${i + 1}`}
                    />
                  </td>
                  <td>
                    <button
                      type="button"
                      onClick={() => setParcelas((prev) => prev.filter((_, idx) => idx !== i))}
                      aria-label={`Remover parcela ${i + 1}`}
                      style={{ background: "none", border: 0, cursor: "pointer", color: "var(--danger-text)" }}
                    >
                      <FiTrash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableWrapper>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <SecondaryButton
            type="button"
            onClick={() => setParcelas((prev) => [...prev, { ...PARCELA_VAZIA }])}
            style={{ padding: "4px 10px", fontSize: "0.78rem" }}
          >
            <FiPlus size={12} /> Adicionar parcela
          </SecondaryButton>
          {parcelas.length > 0 && (
            <Pill $tone={somaOk ? "success" : "warning"}>
              soma: {fmtPct(soma)}%{somaOk ? "" : " (precisa fechar 100%)"}
            </Pill>
          )}
        </div>
        <InlineHint>
          Offset conta {dados.contagem_dias === "UTEIS" ? "dias úteis" : "dias corridos"} a
          partir do evento. Ex.: 70% aprovação + 15% início da produção + 15%
          entrega +7d.
        </InlineHint>
      </Field>

      {erro && <ErrorMsg>{erro}</ErrorMsg>}

      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", alignItems: "center" }}>
        {onDelete && (
          <AsyncButton
            variant="danger"
            silentSuccess
            onClick={onDelete}
            style={{ marginRight: "auto" }}
          >
            Desativar
          </AsyncButton>
        )}
        <SecondaryButton type="button" onClick={onCancel}>Cancelar</SecondaryButton>
        <AsyncButton
          variant="primary"
          onClick={salvar}
          successMessage="Forma de pagamento salva"
          silentError
        >
          Salvar
        </AsyncButton>
      </div>
    </div>
  );
}

/**
 * Secao completa (tabela + slide-over). `podeEditar` vem da pagina pai
 * (comercial:configurar); o backend aceita comercial:configurar OU
 * financeiro:configurar na escrita.
 */
export default function FormasPagamentoSection({ podeEditar, unidadeId }) {
  const { request } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  const [editando, setEditando] = useState(null); // null | "novo" | condicao

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro(null);
    try {
      const payload = await listarCondicoesPagamento(request, { ativo: true });
      const list = Array.isArray(payload) ? payload : payload?.results || [];
      setItems(list);
    } catch (err) {
      setErro(err?.message || "Erro ao carregar formas de pagamento.");
    } finally {
      setLoading(false);
    }
  }, [request]);

  useEffect(() => { carregar(); }, [carregar]);

  const ordenadas = useMemo(
    () => items.slice().sort((a, b) =>
      (b.favorita === a.favorita ? a.nome.localeCompare(b.nome) : b.favorita ? 1 : -1)),
    [items],
  );

  const handleSalvar = async (payload) => {
    if (editando === "novo") await criarCondicaoPagamento(request, payload);
    else await atualizarCondicaoPagamento(request, editando.id, payload);
    setEditando(null);
    await carregar();
  };

  const handleDesativar = async () => {
    if (!window.confirm(`Desativar a forma "${editando.nome}"?`)) return;
    await desativarCondicaoPagamento(request, editando.id);
    setEditando(null);
    await carregar();
  };

  return (
    <Card>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
        <SectionTitle style={{ margin: 0 }}>Formas de pagamento</SectionTitle>
        {podeEditar && (
          <SecondaryButton
            type="button"
            onClick={() => setEditando("novo")}
            style={{ padding: "5px 12px", fontSize: "0.8rem" }}
          >
            <FiPlus size={13} /> Nova forma
          </SecondaryButton>
        )}
      </div>
      <InlineHint>
        Usadas no orçamento (a taxa entra como s2 no motor divisor) e no
        cronograma de recebimento do pedido (parcelas por evento do processo).
      </InlineHint>

      {erro && <ErrorMsg>{erro}</ErrorMsg>}

      {loading ? (
        <EmptyMsg>Carregando formas de pagamento...</EmptyMsg>
      ) : ordenadas.length === 0 ? (
        <EmptyMsg>
          Nenhuma forma cadastrada. Crie "70/15/15 por etapa", "À vista",
          "12x cartão" para reusar em orçamentos e pedidos.
        </EmptyMsg>
      ) : (
        <TableWrapper>
          <Table>
            <colgroup>
              <col style={{ width: 34 }} />
              <col style={{ width: "22%" }} />
              <col />
              <col style={{ width: 90 }} />
              <col style={{ width: 110 }} />
              <col style={{ width: 44 }} />
            </colgroup>
            <thead>
              <tr>
                <th aria-label="Favorita" />
                <th>Forma</th>
                <th>Parcelas</th>
                <th style={{ textAlign: "right" }}>Taxa</th>
                <th>Contagem</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {ordenadas.map((c) => (
                <tr key={c.id}>
                  <td>
                    {c.favorita && (
                      <FiStar
                        size={14}
                        color="var(--warning-solid)"
                        fill="var(--warning-solid)"
                        aria-label="Favorita"
                      />
                    )}
                  </td>
                  <td><strong>{c.nome}</strong></td>
                  <td
                    style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 0 }}
                    title={resumirParcelas(c)}
                  >
                    {resumirParcelas(c)}
                  </td>
                  <td style={{ textAlign: "right" }}>{fmtPct(c.taxa_pagamento_percentual)}%</td>
                  <td>
                    <Pill $tone="neutral">
                      {c.contagem_dias === "UTEIS" ? "dias úteis" : "corridos"}
                    </Pill>
                  </td>
                  <td>
                    {podeEditar && (
                      <button
                        type="button"
                        onClick={() => setEditando(c)}
                        aria-label={`Editar ${c.nome}`}
                        style={{ background: "none", border: 0, cursor: "pointer", color: "inherit", padding: 4 }}
                      >
                        <FiEdit2 size={13} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableWrapper>
      )}

      {editando && (
        <SlideOverPanel
          open
          onClose={() => setEditando(null)}
          title={editando === "novo" ? "Nova forma de pagamento" : `Editar ${editando.nome}`}
          width="min(92vw, 640px)"
        >
          <FormaPagamentoForm
            inicial={editando === "novo" ? null : editando}
            unidadeId={editando === "novo" ? unidadeId : editando.unidade}
            onSave={handleSalvar}
            onCancel={() => setEditando(null)}
            onDelete={editando !== "novo" ? handleDesativar : undefined}
          />
        </SlideOverPanel>
      )}
    </Card>
  );
}
